/**
 * BundlesTab - Tab content for displaying bundles on the collection page
 *
 * Shows a grid of bundle cards with sorting options.
 * Allows creating new bundles for collection owners.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../../ui/components/Button';
import { Select } from '../../ui/components/Select';
import { FeatherPlus, FeatherLoader } from '@subframe/core';
import { BundleCard } from './BundleCard';
import { BundleDetailModal } from './BundleDetailModal';
import { BuyBundleModal } from './BuyBundleModal';
import { CancelBundleModal } from './CancelBundleModal';
import { CreateBundleModal } from './CreateBundleModal';
import {
  getCollectionBundles,
  getCollectionBundleStats,
  getBundleDetails,
  type NFTBundle,
  type BundleStats,
} from '../../api/bundles';
import { useAuthStore } from '../../store/authStore';
import { formatPrice } from '../../utils/formatters';
import type { SelectableNFT } from './NFTMultiSelect';

interface BundlesTabProps {
  collectionId: string;
  collectionName: string;
  userNfts?: SelectableNFT[];
  onRefresh?: () => void;
}

type SortOption = 'created_desc' | 'price_asc' | 'price_desc' | 'price_per_nft_asc' | 'price_per_nft_desc';

export const BundlesTab: React.FC<BundlesTabProps> = ({
  collectionId,
  collectionName,
  userNfts = [],
  onRefresh,
}) => {
  const { user } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [bundles, setBundles] = useState<NFTBundle[]>([]);
  const [stats, setStats] = useState<BundleStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [sortBy, setSortBy] = useState<SortOption>('price_per_nft_asc');

  // Modal state
  const [selectedBundle, setSelectedBundle] = useState<NFTBundle | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Check if user has enough NFTs in this collection to create a bundle (minimum 2)
  const userCanCreateBundle = userNfts.length >= 2;

  // Handle bundle query param for deep linking
  useEffect(() => {
    const bundleId = searchParams.get('bundle');
    if (bundleId && !showDetailModal && !selectedBundle) {
      // Try to find bundle in loaded bundles first (only live bundles are in the list)
      const existingBundle = bundles.find(b => b.id === bundleId);
      if (existingBundle) {
        setSelectedBundle(existingBundle);
        setShowDetailModal(true);
      } else if (!loading) {
        // Fetch bundle details if not in the list
        getBundleDetails(bundleId)
          .then(result => {
            // Only show modal for live bundles
            // Cancelled, expired, or sold bundles should just show the tab
            if (result.bundle.status === 'live') {
              setSelectedBundle(result.bundle);
              setShowDetailModal(true);
            } else {
              // Bundle exists but not available, clear the param
              searchParams.delete('bundle');
              setSearchParams(searchParams, { replace: true });
            }
          })
          .catch(() => {
            // Bundle not found, clear the param
            searchParams.delete('bundle');
            setSearchParams(searchParams, { replace: true });
          });
      }
    }
  }, [searchParams, bundles, loading, showDetailModal, selectedBundle, setSearchParams]);

  // Load bundles
  const loadBundles = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const [bundlesResult, statsResult] = await Promise.all([
        getCollectionBundles(collectionId, { limit: 50, orderBy: sortBy }),
        getCollectionBundleStats(collectionId),
      ]);

      setBundles(bundlesResult.bundles);
      setStats(statsResult);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load bundles');
    } finally {
      setLoading(false);
    }
  }, [collectionId, sortBy]);

  useEffect(() => {
    loadBundles();
  }, [loadBundles]);

  // Handle bundle click - fetch full details (list only includes 4 preview items)
  const handleBundleClick = async (bundle: NFTBundle) => {
    // Show modal immediately with preview data
    setSelectedBundle(bundle);
    setShowDetailModal(true);
    searchParams.set('bundle', bundle.id);
    setSearchParams(searchParams, { replace: true });

    // Fetch full bundle details with all items
    try {
      const result = await getBundleDetails(bundle.id);
      setSelectedBundle(result.bundle);
    } catch {
      // Keep preview data if detail fetch fails
    }
  };

  // Handle closing bundle modal
  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedBundle(null);
    // Remove bundle param from URL
    searchParams.delete('bundle');
    setSearchParams(searchParams, { replace: true });
  };

  // Handle buy click
  const handleBuyClick = () => {
    setShowDetailModal(false);
    setShowBuyModal(true);
  };

  // Handle cancel click (for owners)
  const handleCancelClick = () => {
    setShowDetailModal(false);
    setShowCancelModal(true);
  };

  // Handle cancel success
  const handleCancelSuccess = () => {
    setShowCancelModal(false);
    setSelectedBundle(null);
    loadBundles();
    onRefresh?.();
  };

  // Handle purchase success
  const handlePurchaseSuccess = () => {
    setShowBuyModal(false);
    setSelectedBundle(null);
    loadBundles();
    onRefresh?.();
  };

  // Handle bundle creation success
  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    loadBundles();
    onRefresh?.();
  };

  const isOwner = (bundle: NFTBundle): boolean =>
    !!(user?.xrpl_address && bundle.sellerAddress === user.xrpl_address);

  return (
    <div className="flex flex-col gap-6 w-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-heading-3 font-heading-3 text-default-font">
            NFT Bundles
          </span>
          {stats && stats.liveCount > 0 && (
            <span className="text-body text-subtext-color">
              {stats.liveCount} available
            </span>
          )}
        </div>

        <div className="flex items-center gap-3">
          {/* Sort */}
          <Select
            value={sortBy}
            onValueChange={(value) => setSortBy(value as SortOption)}
          >
            <Select.Item value="created_desc">Newest First</Select.Item>
            <Select.Item value="price_asc">Price: Low to High</Select.Item>
            <Select.Item value="price_desc">Price: High to Low</Select.Item>
            <Select.Item value="price_per_nft_asc">Per NFT: Low to High</Select.Item>
            <Select.Item value="price_per_nft_desc">Per NFT: High to Low</Select.Item>
          </Select>

          {/* Create bundle button - requires at least 2 NFTs */}
          {user && userCanCreateBundle && (
            <Button
              variant="brand-primary"
              size="medium"
              icon={<FeatherPlus />}
              onClick={() => setShowCreateModal(true)}
            >
              Create Bundle
            </Button>
          )}
        </div>
      </div>

      {/* Stats bar */}
      {stats && (stats.soldCount > 0 || stats.totalVolumeXrp > 0) && (
        <div className="flex items-center gap-6 p-4 bg-neutral-50 rounded-lg">
          {stats.lowestPriceXrp && (
            <div className="flex flex-col">
              <span className="text-caption text-subtext-color">Lowest Price</span>
              <span className="text-body-bold text-default-font">
                {formatPrice(stats.lowestPriceXrp)}
              </span>
            </div>
          )}
          {stats.soldCount > 0 && (
            <div className="flex flex-col">
              <span className="text-caption text-subtext-color">Bundles Sold</span>
              <span className="text-body-bold text-default-font">{stats.soldCount}</span>
            </div>
          )}
          {stats.totalVolumeXrp > 0 && (
            <div className="flex flex-col">
              <span className="text-caption text-subtext-color">Total Volume</span>
              <span className="text-body-bold text-default-font">
                {formatPrice(stats.totalVolumeXrp)}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <FeatherLoader className="w-6 h-6 animate-spin text-brand-600" />
        </div>
      ) : error ? (
        <div className="flex flex-col items-center gap-4 py-12">
          <p className="text-body text-error-600">{error}</p>
          <Button variant="neutral-secondary" onClick={loadBundles}>
            Try Again
          </Button>
        </div>
      ) : bundles.length === 0 ? (
        <div className="flex flex-col items-center gap-4 py-12">
          <p className="text-body text-subtext-color text-center">
            No bundles available for this collection yet.
          </p>
          {user && userCanCreateBundle && (
            <Button
              variant="brand-secondary"
              onClick={() => setShowCreateModal(true)}
            >
              Create the First Bundle
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {bundles.map((bundle) => (
            <BundleCard
              key={bundle.id}
              bundle={bundle}
              onClick={() => handleBundleClick(bundle)}
              showSeller
            />
          ))}
        </div>
      )}

      {/* Bundle Detail Modal */}
      {selectedBundle && (
        <BundleDetailModal
          isOpen={showDetailModal}
          onClose={handleCloseDetailModal}
          bundle={selectedBundle}
          onBuy={handleBuyClick}
          onCancel={handleCancelClick}
          canBuy={!!user}
          isOwner={isOwner(selectedBundle)}
        />
      )}

      {/* Buy Bundle Modal */}
      {selectedBundle && (
        <BuyBundleModal
          isOpen={showBuyModal}
          onClose={() => {
            setShowBuyModal(false);
            setSelectedBundle(null);
          }}
          bundle={selectedBundle}
          onSuccess={handlePurchaseSuccess}
        />
      )}

      {/* Cancel Bundle Modal */}
      {selectedBundle && (
        <CancelBundleModal
          isOpen={showCancelModal}
          onClose={() => {
            setShowCancelModal(false);
            setSelectedBundle(null);
          }}
          bundle={selectedBundle}
          onSuccess={handleCancelSuccess}
        />
      )}

      {/* Create Bundle Modal */}
      <CreateBundleModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        collectionId={collectionId}
        collectionName={collectionName}
        userNfts={userNfts}
        onSuccess={handleCreateSuccess}
      />
    </div>
  );
};

export default BundlesTab;
